import React from 'react';
import { LuFileEdit, LuCalendar, LuClock, LuChevronLeft, LuShare2, LuBookmark, LuArrowRight } from 'react-icons/lu';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { PageWrapper } from '../components/PageWrapper';

const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  const baseUrl = axios.defaults.baseURL || (window.location.hostname === 'localhost' ? 'http://localhost:5000' : '');
  return `${baseUrl}${url}`;
};

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const { data: post, isLoading, isError } = useQuery({
    queryKey: ['blog-post', slug],
    queryFn: () => axios.get(`/api/blog/${slug}`).then(r => r.data)
  });

  if (isLoading) return (
    <div className="flex-center" style={{ height: '100vh', background: 'var(--cream-light)' }}>
      <div className="loading-spinner" />
    </div>
  );

  if (isError || !post) return (
    <div style={{ padding: '120px 40px', textAlign: 'center', background: 'var(--cream-light)', minHeight: '80vh' }}>
      <h2 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '2rem', color: 'var(--forest)', marginBottom: 20 }}>Post not found</h2>
      <p style={{ color: 'var(--text-muted)', marginBottom: 32 }}>The story you're looking for might have been moved or deleted.</p>
      <Link to="/blog" className="btn btn-primary">Return to Journal</Link>
    </div>
  );

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.excerpt,
    "image": post.coverImage?.url,
    "author": { "@type": "Person", "name": post.author || "Nimmy Raphi" },
    "publisher": {
      "@type": "Organization",
      "name": "Villa Tesori",
      "logo": { "@type": "ImageObject", "url": "https://villatesori.com/logo-green.jpeg" }
    },
    "datePublished": post.publishedAt || post.createdAt,
    "dateModified": post.updatedAt || post.publishedAt || post.createdAt,
    "mainEntityOfPage": { "@type": "WebPage", "@id": `https://villatesori.com/blog/${post.slug}` },
    "keywords": post.tags?.join(', ')
  };

  return (
    <PageWrapper>
      <Helmet>
        <title>{post.metaTitle || `${post.title} – Villa Tesori Journal`}</title>
        <meta name="description" content={post.metaDescription || post.excerpt} />
        <meta name="keywords" content={post.tags?.join(', ')} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:image" content={post.coverImage?.url} />
        <meta property="og:type" content="article" />
        <meta name="article:author" content={post.author || 'Nimmy Raphi'} />
        <meta name="article:published_time" content={post.publishedAt || post.createdAt} />
        <meta name="article:tag" content={post.tags?.join(', ')} />
        <link rel="canonical" href={`https://villatesori.com/blog/${post.slug}`} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>

      {/* Hero Section */}
      <div className="blog-post-hero" style={{ height: '70vh', minHeight: 400, position: 'relative', overflow: 'hidden', marginTop: 0 }}>
        <img 
          src={getImageUrl(post.coverImage?.url) || 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1600'} 
          alt={post.coverImage?.alt || post.title} 
          style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
        />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(26,46,26,0.9) 0%, rgba(26,46,26,0.4) 50%, rgba(26,46,26,0.2) 100%)' }} />
        
        <div style={{ position: 'absolute', top: 100, left: 0, right: 0, zIndex: 10 }}>
          <div className="container">
            <button 
              onClick={() => navigate('/blog')}
              style={{ background: 'none', border: 'none', color: 'var(--white)', display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.82rem', letterSpacing: '0.1em', cursor: 'pointer', opacity: 0.8 }}
            >
              <LuChevronLeft size={18} /> BACK TO JOURNAL
            </button>
          </div>
        </div>

        <div style={{ position: 'absolute', bottom: 64, left: 0, right: 0 }}>
          <div className="container-sm">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <span className="badge badge-gold" style={{ textTransform: 'uppercase', padding: '6px 16px', borderRadius: 0 }}>{post.category}</span>
              <span style={{ height: 1, width: 40, background: 'rgba(255,255,255,0.3)' }} />
              <span style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.7)', letterSpacing: '0.1em' }}>{post.readTime} MIN READ</span>
            </div>
            <h1 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(2.4rem, 6vw, 4.2rem)', color: 'var(--white)', fontWeight: 300, lineHeight: 1.1, marginBottom: 24 }}>
              {post.title}
            </h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: 24, color: 'rgba(245,240,232,0.85)', fontSize: '0.88rem', fontFamily: 'Jost, sans-serif' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--gold)', color: 'var(--forest)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: 600 }}>NR</div>
                <span>By {post.author || 'Nimmy Raphi'}</span>
              </div>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><LuCalendar size={16} /> {new Date(post.publishedAt || post.createdAt).toLocaleDateString('en-IN', { day:'numeric', month:'long', year:'numeric' })}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Article Content */}
      <section className="section" style={{ background: 'var(--cream-light)', paddingTop: 80 }}>
        <div className="container-sm">
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 300px', gap: 80 }}>
            {/* Main Content */}
            <article>
              <div style={{ marginBottom: 48 }}>
                <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.4rem', fontStyle: 'italic', color: 'var(--forest)', lineHeight: 1.7, marginBottom: 0, position: 'relative', paddingLeft: 40 }}>
                   <span style={{ position: 'absolute', left: 0, top: -10, fontSize: '4rem', color: 'var(--gold-pale)', opacity: 0.5, fontFamily: 'serif' }}>"</span>
                   {post.excerpt}
                </p>
              </div>

              {/* Featured Video */}
              {post.video?.url && (
                <div style={{ marginBottom: 48 }}>
                  <video 
                    controls 
                    preload="auto"
                    playsInline
                    style={{ width: '100%', borderRadius: 8, background: '#000', boxShadow: '0 20px 40px rgba(0,0,0,0.1)' }}
                    src={post.video.url}
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>
              )}

              <div
                className="blog-content"
                dangerouslySetInnerHTML={{ __html: post.content }}
              />

              {/* Tags & Share */}
              <div style={{ marginTop: 64, paddingTop: 40, borderTop: '1px solid var(--mist)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 24 }}>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {post.tags?.map(tag => (
                    <span key={tag} style={{ fontSize: '0.75rem', background: 'rgba(26,46,26,0.05)', color: 'var(--forest)', padding: '6px 14px', borderRadius: 0, letterSpacing: '0.05em' }}>#{tag}</span>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 16 }}>
                  <button className="icon-btn" title="Share Article"><LuShare2 size={20} /></button>
                  <button className="icon-btn" title="Save for later"><LuBookmark size={20} /></button>
                </div>
              </div>

              {/* Author Bio */}
              <div className="author-bio" style={{ marginTop: 60, background: 'var(--white)', padding: '40px', border: '1px solid var(--mist)', display: 'flex', gap: 32, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'var(--forest)', color: 'var(--gold-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Cormorant Garamond, serif', fontSize: '2rem', flexShrink: 0 }}>NR</div>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: '0.2em', marginBottom: 8, fontWeight: 600 }}>About the Author</div>
                  <h4 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.6rem', color: 'var(--forest)', marginBottom: 12 }}>{post.author || 'Nimmy Raphi'}</h4>
                  <p style={{ fontSize: '0.92rem', color: 'var(--text-muted)', lineHeight: 1.8 }}>Nimmy is your host at Villa Tesori. An avid explorer of the Western Ghats, she shares her local wisdom and love for Wayanad's hidden trails and culinary secrets through these stories.</p>
                </div>
              </div>
            </article>

            {/* Sidebar */}
            <aside>
              <div style={{ position: 'sticky', top: 120 }}>
                <div style={{ background: 'var(--forest)', padding: '40px 32px', textAlign: 'center', color: 'var(--white)', marginBottom: 40 }}>
                  <div style={{ fontFamily: 'Pinyon Script, cursive', fontSize: '2.4rem', color: 'var(--gold-light)', marginBottom: 16 }}>Experience the Magic</div>
                  <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem', lineHeight: 1.7, marginBottom: 24 }}>Stay in the heart of Vellarimala and discover these stories for yourself.</p>
                  <Link to="/contact" className="btn btn-gold" style={{ width: '100%', justifyContent: 'center' }}>Reserve Your Stay</Link>
                </div>

                <div style={{ padding: '0 10px' }}>
                  <h4 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.4rem', color: 'var(--forest)', marginBottom: 24, borderBottom: '1px solid var(--gold-pale)', paddingBottom: 12 }}>Latest Updates</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                    {/* Related posts logic could go here, for now just a link back */}
                    <p style={{ fontSize: '0.88rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>Discover more stories about Wayanad's culture, food, and adventure.</p>
                    <Link to="/blog" style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--gold)', textDecoration: 'none', fontSize: '0.82rem', fontWeight: 600, letterSpacing: '0.1em' }}>
                      EXPLORE ALL POSTS <LuArrowRight size={16} />
                    </Link>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <style>{`
        .blog-content h2 { font-family:'Cormorant Garamond',serif; font-size:2.2rem; color:var(--forest); margin:56px 0 24px; font-weight:400; line-height:1.2; }
        .blog-content h3 { font-family:'Cormorant Garamond',serif; font-size:1.6rem; color:var(--forest); margin:40px 0 16px; font-weight:400; }
        .blog-content p { color:var(--text-body); line-height:2; margin-bottom:24px; font-size:1.1rem; }
        .blog-content strong { color:var(--forest); font-weight:600; }
        .blog-content a { color:var(--gold); text-decoration: underline; text-underline-offset: 4px; }
        .blog-content ul, .blog-content ol { padding-left:24px; margin-bottom:28px; }
        .blog-content li { line-height:1.9; color:var(--text-body); margin-bottom:12px; font-size:1.05rem; }
        .blog-content img { max-width: 100%; height: auto; border-radius: 4px; margin: 32px 0; box-shadow: 0 10px 30px rgba(0,0,0,0.05); }
        .blog-content video { max-width: 100%; border-radius: 8px; margin: 32px 0; display: block; box-shadow: 0 10px 40px rgba(0,0,0,0.1); }
        .blog-content iframe { width: 100%; aspect-ratio: 16 / 9; border-radius: 8px; margin: 40px 0; border: none; box-shadow: 0 20px 40px rgba(0,0,0,0.1); }
        .icon-btn { background: var(--white); border: 1px solid var(--mist); width: 44px; height: 44px; display: flex; alignItems: center; justifyContent: center; cursor: pointer; transition: all 0.3s; color: var(--forest); }
        .icon-btn:hover { background: var(--forest); color: var(--gold-light); }
        @media (max-width: 1000px) {
          div[style*="grid-template-columns"] { grid-template-columns: 1fr !important; gap: 60px !important; }
          aside { order: 2; }
        }
      `}</style>
    </PageWrapper>
  );
}

